import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  AFTER_MIDNIGHT_VARIANTS,
  DEFAULT_AFTER_MIDNIGHT_VARIANT,
} from 'utils/afterMidnightVariant';
import { Icon } from '@reservamos/elements';
import { useSelector } from 'react-redux';
import normalizeSearchWidgetDateFormat from 'utils/normalizeSearchWidgetDateFormat';
import { selectSearchWidgetDateFormat } from 'selectors/whitelabelSelectors';

/** The library's moon ships with #000 burnt into its path and is loaded as a data URI, so `Icon`
 * renders it as an `<img>` and no colour — neither its own `color` prop nor any CSS — reaches the
 * fill. Every surface it sits on therefore has to be light. If Elements ever ships a colourable
 * moon (the icons imported without a file extension do take `color`), the discs can go dark and the
 * crescent can take the brand's night palette. */
const MOON_SIZE = 'M';

/** Fallback for brands that configure no `searchWidget.dateFormat`; the brand's own order wins when
 * there is one. Same default shape the main listing's heading falls back to. */
const DATE_FORMAT = 'DD-MMM-YY';

/** The pill drops the year: it names a day, not a date on a ticket. */
const SHORT_DATE_FORMAT = 'DD-MMM';

/** Breathing room and rhythm, shared by the three: XL above, M below on the app's own scale. */
const BASE = 'flex w-full items-center gap-3 mt-10 mb-5';

/** Both dimensions are pinned so the disc is a circle, not an oval. `self-stretch` with
 * `aspect-square` does not work here: the height would come from the stretch but the width from the
 * icon's own content, because a `flex-none` item takes its base size from its contents. 40px is the
 * height the two copy lines produce, so the disc still fills the banner as the mockup shows. */
const BANNER_COPY = 'z-10 min-w-0 leading-tight';

/** Star field for the dark banner. Real elements rather than a background image, so the density
 * and placement stay visible and tweakable here instead of buried in a gradient string. */
const STARS = [
  'left-[22%] top-[30%]',
  'left-[47%] top-[68%]',
  'left-[69%] top-[26%]',
  'left-[85%] top-[60%]',
  'left-[34%] top-[78%]',
];

/**
 * The condensed presentation: one line in a pill, with its own copy.
 *
 * Its own component, and not a branch of the divider, because everything it needs — the window
 * start, the condensed format, its two copy keys — is used by nothing else, and keeping it inline
 * pushed the divider past the complexity the linter allows.
 *
 * @param {object} props - Component props.
 * @param {string} props.date - Departure of the section's earliest trip.
 * @param {string} props.windowFrom - Start of the window, or null when the payload omits it.
 * @param {string} props.dateFormat - The brand's date order, already normalized and condensed.
 * @returns {JSX.Element} The pill.
 */
const AfterMidnightPill = ({ date, windowFrom, dateFormat }) => {
  const { t } = useTranslation('trips');

  // `LT`, not a literal: `initMoment` already resolves it to `hh:mm A` or `HH:mm` from the brand's
  // `HOUR_FORMAT`, in all six locales. A hardcoded `HH:mm` would print `00:00` in the pill while
  // the cards right below print `12:00 AM`.
  const time = windowFrom ? moment(windowFrom).format('LT') : null;
  // Its own copy, not a condensation of the banners': the pill leads with the small-hours word and
  // then the day, where the banners lead with the heading. Same message, different sentence.
  const shortDate = moment(date).format(`ddd, ${dateFormat}`).toLowerCase();
  const pillTitle = t('after_midnight_divider_minimal_title', { date: shortDate });
  const pillDetail = time ? t('after_midnight_divider_minimal_detail', { time }) : null;

  return (
    <div className={`${BASE} gap-2.5`} data-testid="after-midnight-divider">
      <span className="h-px flex-1 bg-[#C7C7C7]" />
      <span className="inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-full border border-[#C7C7C7] bg-[#EFEEF8] px-3.5 py-1">
        {/* Same 20px as the banners: the asset is a filled path with no stroke to thicken, so
            scale is the only lever on how heavy it reads. */}
        <span className="flex scale-[0.8] items-center justify-center">
          <Icon type="Night" size={MOON_SIZE} />
        </span>
        <span className="text-base font-bold text-[#232a56]">{pillTitle}</span>
        {pillDetail && (
          <>
            <span aria-hidden="true" className="h-3.5 w-px bg-[#C7C7C7]" />
            <span className="text-sm font-normal text-[#5b628f]">{pillDetail}</span>
          </>
        )}
      </span>
      <span className="h-px flex-1 bg-[#C7C7C7]" />
    </div>
  );
};

AfterMidnightPill.propTypes = {
  date: PropTypes.string.isRequired,
  windowFrom: PropTypes.string,
  dateFormat: PropTypes.string.isRequired,
};

AfterMidnightPill.defaultProps = {
  windowFrom: null,
};

/**
 * Divider that opens the after-midnight section.
 *
 * It IS the section's heading — there is no separate title above it — and it is the only part of
 * the section whose look is still under review, so the three proposals ship together behind a flag.
 *
 * The three say the same thing; only the wrapper changes. They share one set of translation keys:
 * `prominent` and `soft` spell the message out over two lines, `minimal` condenses it into a single
 * centred line, because a pill of roughly 45 characters cannot hold what the banners carry. That is
 * why this takes the date and the window start as data and composes its own text, rather than
 * receiving a finished string.
 *
 * @param {object} props - Component props.
 * @param {string} props.date - Departure of the section's earliest trip, ISO-8601 without offset.
 * @param {string} props.windowFrom - Start of the window, ISO-8601 without offset.
 * @param {'prominent'|'soft'|'minimal'} props.variant - Which presentation to render.
 * @returns {JSX.Element} The divider.
 */
const AfterMidnightDivider = ({ date, windowFrom, variant }) => {
  const { t } = useTranslation('trips');
  // The format alone, not the `env` object it lives in: that selector hands back a fresh `{}`
  // while there is no config, and subscribing to it would re-render this on every dispatch.
  const brandDateFormat = useSelector(selectSearchWidgetDateFormat);

  // A value outside the three falls back rather than rendering nothing: the flag is set per brand
  // by hand, and a typo there must not silently remove the section's heading.
  const resolved = AFTER_MIDNIGHT_VARIANTS.includes(variant)
    ? variant
    : DEFAULT_AFTER_MIDNIGHT_VARIANT;

  // Rendered from the trip's own timestamp, never from the payload's `date` field, so the heading
  // and the hours on the cards below cannot disagree. The backend emits both without an offset, so
  // moment reads them as wall time and neither depends on where the browser is.
  // Normalized from the brand's own `searchWidget.dateFormat`, the same way the listing's heading
  // does it (`ProviderDepartures.js:966`) and `TicketInfoBar.js:46` before it. A literal here would
  // print day-first on a brand configured month-first, on the same screen as a heading that got it
  // right — which is exactly the divergence this helper exists to prevent.
  const dateFormat = normalizeSearchWidgetDateFormat(brandDateFormat, DATE_FORMAT);
  const formattedDate = moment(date).format(dateFormat).toLowerCase();
  const title = t('after_midnight_divider_title');
  const subtitle = t('after_midnight_divider_subtitle', { date: formattedDate });

  if (resolved === 'minimal') {
    return (
      <AfterMidnightPill
        date={date}
        windowFrom={windowFrom}
        dateFormat={normalizeSearchWidgetDateFormat(brandDateFormat, SHORT_DATE_FORMAT)}
      />
    );
  }

  const isProminent = resolved === 'prominent';

  return (
    <div
      className={`${BASE} rounded-xl ${
        isProminent
          ? 'relative overflow-hidden bg-gradient-to-br from-[#171c3a] via-[#232a56] to-[#1b2145] px-4 py-2'
          : 'border border-[#C7C7C7] bg-[#EFEEF8] px-4 py-[7px]'
      }`}
      data-testid="after-midnight-divider"
    >
      {isProminent &&
        STARS.map((position) => (
          <span
            key={position}
            aria-hidden="true"
            className={`pointer-events-none absolute ${position} h-px w-px rounded-full bg-white/80 shadow-[0_0_2px_1px_rgba(255,255,255,0.7)]`}
          />
        ))}

      {/* A light disc, because `Night` and `EarlyMorning` both ship with #000 burnt into the path:
          the mark carries the contrast, since the icon cannot carry a colour. */}
      <span className="z-10 flex h-10 w-10 flex-none items-center justify-center rounded-full bg-white">
        {/* Elements' scale jumps 15 -> 25 with nothing between: 25 crowds the disc, 15 is lost
            in it. Scaling the 25 by 0.8 lands on 20px. */}
        <span className="flex scale-[0.8] items-center justify-center">
          <Icon type="Night" size={MOON_SIZE} />
        </span>
      </span>

      {/* The two lines are one statement, not two paragraphs, so they sit tight together. */}
      <div className={BANNER_COPY}>
        <p className={`text-base font-bold ${isProminent ? 'text-white' : 'text-[#232a56]'}`}>
          {title}
        </p>
        <p className={`text-sm font-normal ${isProminent ? 'text-white/90' : 'text-[#5b628f]'}`}>
          {subtitle}
        </p>
      </div>
    </div>
  );
};

AfterMidnightDivider.propTypes = {
  date: PropTypes.string.isRequired,
  windowFrom: PropTypes.string,
  variant: PropTypes.oneOf(AFTER_MIDNIGHT_VARIANTS),
};

AfterMidnightDivider.defaultProps = {
  windowFrom: null,
  variant: DEFAULT_AFTER_MIDNIGHT_VARIANT,
};

export default AfterMidnightDivider;
